import { IntroProvider } from "./motion/intro";
import React, { useEffect, Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import NotFoundPage from './pages/NotFoundPage'
import Layout from './components/Layout'
import SEO from './components/SEO'
import LazySection from './components/LazySection'
import BadanibexHero from './components/BadanibexHero'
import { useTranslation } from './hooks/useTranslation'
import { useAuth } from './contexts/AuthContext'
import { ping } from './services/api'
import CouponFloat from './components/CouponFloat'
import './App.css'

// Below-fold sections: lazy loaded to reduce initial bundle
const PartnersBar = lazy(() => import('./components/PartnersBar'))
const Solutions = lazy(() => import('./components/Solutions'))
const WhyChooseUs = lazy(() => import('./components/WhyChooseUs'))
const Metrics = lazy(() => import('./components/Metrics'))
const Process = lazy(() => import('./components/Process'))
const BlogSection = lazy(() => import('./components/BlogSection'))
const FinalCTA = lazy(() => import('./components/FinalCTA'))
const FeaturedProductsLazy = lazy(() => import(/* webpackChunkName: "featured-products" */ './components/FeaturedProducts'))
const TestimonialsSectionLazy = lazy(() => import(/* webpackChunkName: "testimonials-section" */ './components/TestimonialsSection'))

const FeaturedSkeleton = () => (
  <div className="min-h-[520px] animate-pulse" style={{ backgroundColor: 'var(--surface)' }} aria-hidden="true" />
)

const TestimonialsSkeleton = () => (
  <div className="min-h-[380px] animate-pulse" style={{ backgroundColor: 'var(--surface)' }} aria-hidden="true" />
)

const SectionFallback = () => (
  <div className="min-h-[300px] animate-pulse" style={{ backgroundColor: 'var(--surface)' }} aria-hidden="true" />
)

function HomePage() {
  const { currentLanguage } = useTranslation()
  const { user } = useAuth()

  useEffect(() => {
    ping().then(ok => console.log('ping:', ok))
  }, [])

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'badanibex',
    description: currentLanguage === 'ar'
      ? 'منصة تعرض منتجات رقمية وأنظمة ويب حديثة تشمل أدوات أتمتة، أنظمة SaaS، ولوحات تحكم احترافية'
      : 'A platform displaying modern digital products and web systems including automation tools, SaaS systems, and professional dashboards',
    url: 'https://badanibex.com',
    inLanguage: currentLanguage === 'ar' ? 'ar-SA' : 'en-US',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://badanibex.com/catalog?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    },
    publisher: {
      '@type': 'Person',
      name: 'Fahd Ali',
      url: 'https://badanibex.com/about'
    }
  }

  return (
    <Layout>
      <SEO
        title={currentLanguage === 'ar'
          ? 'badanibex قوالب ويب ولوحات تحكم وموارد إبداعية للاستخدام المباشر'
          : 'badanibex - Web Templates, Dashboards & Creative Resources'}
        description={currentLanguage === 'ar'
          ? 'ارتقِ بقدراتك الإبداعية وأطلق منصتك الرقمية بسرعة أكبر مع قوالب ويب ولوحات تحكم وأنظمة SaaS وتطبيقات وموارد إبداعية جاهزة للاستخدام. اختصر وقت التطوير وابنِ مشاريعك الرقمية باحترافية مع badanibex.'
          : 'Boost your creative capabilities and launch your digital platform faster with ready-to-use web templates, dashboards, SaaS systems, and creative resources. Accelerate development with badanibex.'}
        keywords={currentLanguage === 'ar'
          ? 'badanibex, قوالب ويب, قوالب مواقع, لوحات تحكم, أنظمة SaaS, تطبيقات ويب, أنظمة ويب, منتجات رقمية, موارد إبداعية, قوالب برمجية, واجهات ويب, مشاريع جاهزة, Laravel, React, Dashboard, SaaS Templates, Web Templates'
          : 'badanibex, web templates, website templates, dashboards, SaaS systems, web apps, digital products, creative resources, code templates, web interfaces, Laravel, React, Dashboard, SaaS Templates'}
        url="https://badanibex.com"
        locale={currentLanguage === 'ar' ? 'ar_SA' : 'en_US'}
        structuredData={structuredData}
      />
      {/* Hero — direct import, always above fold, no lazy wrapper */}
      <BadanibexHero />

      {!user && (
        <CouponFloat coupons={[{ code: 'LOYAL35', value: 35, type: 'percent' }]} />
      )}

      {/* Featured Products */}
      <LazySection skeleton={<FeaturedSkeleton />} minHeight="520px" rootMargin="150px">
        <Suspense fallback={<FeaturedSkeleton />}>
          <FeaturedProductsLazy />
        </Suspense>
      </LazySection>

      {/* Partners */}
      <LazySection skeleton={<SectionFallback />} minHeight="120px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <PartnersBar />
        </Suspense>
      </LazySection>

      {/* Solutions */}
      <LazySection skeleton={<SectionFallback />} minHeight="300px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <Solutions />
        </Suspense>
      </LazySection>

      {/* Why Choose Us */}
      <LazySection skeleton={<SectionFallback />} minHeight="300px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <WhyChooseUs />
        </Suspense>
      </LazySection>

      {/* Metrics */}
      <LazySection skeleton={<SectionFallback />} minHeight="200px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <Metrics />
        </Suspense>
      </LazySection>

      {/* Process */}
      <LazySection skeleton={<SectionFallback />} minHeight="300px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <Process />
        </Suspense>
      </LazySection>

      {/* Testimonials */}
      <LazySection skeleton={<TestimonialsSkeleton />} minHeight="380px" rootMargin="150px">
        <Suspense fallback={<TestimonialsSkeleton />}>
          <TestimonialsSectionLazy />
        </Suspense>
      </LazySection>

      {/* Blog */}
      <LazySection skeleton={<SectionFallback />} minHeight="200px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <BlogSection />
        </Suspense>
      </LazySection>

      {/* Final CTA */}
      <LazySection skeleton={<SectionFallback />} minHeight="200px" rootMargin="200px">
        <Suspense fallback={<SectionFallback />}>
          <FinalCTA />
        </Suspense>
      </LazySection>
    </Layout>
  )
}

export default function App() {
  return (
    <IntroProvider>
      <Routes>
        <Route path="/*" element={<HomePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </IntroProvider>
  )
}